import { produce } from "immer";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { GetExperienceResponseBodyType } from "@intelligems/ig-types/functions/experiences/get-experience";
import { GetUserInterfacesResponseBodyType } from "@intelligems/ig-types/functions/user-interfaces/get-user-interfaces";
import { GetWidgetsResponseBodyType } from "@intelligems/ig-types/functions/widgets/get-widgets";
import { PutWidgetResponseBodyType } from "@intelligems/ig-types/functions/widgets/put-widget";
import { WidgetDto, WidgetType } from "@intelligems/ig-types/dtos/widgets";
import { getMutationErrorMessage } from "@src/queryClient";
import { updateWidgetAfterPut } from "@src/store/slices/widgets";
import { updateUserInterfaceWidgets } from "@src/utils/userInterface/updateUserInterfaceWidgets";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "../api";
import { userInterfaceKeys, widgetKeys } from "../queryKeys";

const putWidget = async (data: Partial<WidgetDto>) => {
  const resp = await api.put<PutWidgetResponseBodyType>(
    `/v2/widgets/${data.id}`,
    { widget: data }
  );
  return resp.data;
};

export const usePutWidget = (widgetType: WidgetType) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<PutWidgetResponseBodyType, Error, Partial<WidgetDto>>({
    mutationKey: [`mutateWidget-${widgetType}`],
    mutationFn: async (data: Partial<WidgetDto>) => putWidget(data),
    onSuccess: (data) => {
      updateCache(queryClient, data.widget);
      dispatch(updateWidgetAfterPut(data.widget));
      toast.success("Successfully saved widget!");
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to save changes${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};

function updateCache(queryClient: QueryClient, widget: WidgetDto) {
  try {
    updateWidgetsCache(queryClient, widget);
    updateUserInterfacesCache(queryClient, widget);
    updateExperiencesCache(queryClient, widget);
  } catch (error) {
    console.error("Error updating cache", error);
  }
}

function updateWidgetsCache(queryClient: QueryClient, widget: WidgetDto) {
  const data = queryClient.getQueryData<GetWidgetsResponseBodyType>(
    widgetKeys.all
  );
  if (!data?.widgets) {
    return;
  }

  const newData = produce(data, (draft) => {
    const index = draft.widgets.findIndex((w) => w.id === widget.id);
    if (index > -1) {
      draft.widgets[index] = widget;
    } else {
      draft.widgets.push(widget);
    }
  });
  if (newData !== data) {
    queryClient.setQueryData(widgetKeys.all, newData);
  }
}

function updateUserInterfacesCache(
  queryClient: QueryClient,
  widget: WidgetDto
) {
  const data = queryClient.getQueryData<GetUserInterfacesResponseBodyType>(
    userInterfaceKeys.all
  );
  if (!data?.userInterfaces) {
    return;
  }

  const newData = produce(data, (draft) => {
    updateUserInterfaceWidgets(draft.userInterfaces, widget);
  });
  if (newData !== data) {
    queryClient.setQueryData(userInterfaceKeys.all, newData);
  }
}

function updateExperiencesCache(queryClient: QueryClient, widget: WidgetDto) {
  const entries = queryClient.getQueriesData<GetExperienceResponseBodyType>({
    queryKey: [
      {
        scope: "experience",
      },
    ],
  });
  for (const [key, data] of entries) {
    if (!data?.experience) {
      continue;
    }

    const newData = produce(data, (draft) => {
      for (const variation of draft.experience.variations ?? []) {
        updateUserInterfaceWidgets(variation.userInterfaces, widget);
      }
    });
    if (newData !== data) {
      queryClient.setQueryData(key, newData);
    }
  }
}
